import authenticationRoutes from './credential-auth.routes';
import { APP_TAB } from '@/shared/constants/app';
import { TREATMENT_CONTENT_SOURCE } from '@/features/resources/constants/treatment-content';

const routes = [
  { path: '/', redirect: '/home' },
  {
    path: '/onboarding/terms',
    component: () => import('@/features/profile/components/TermsView.vue'),
    meta: { requiresAuth: true, onboarding: true },
  },
  {
    path: '/onboarding/setup',
    component: () => import('@/features/profile/components/ProfileSetupView.vue'),
    meta: { requiresAuth: true, onboarding: true },
  },
  {
    path: '/home',
    component: () => import('@/features/home/components/HomeView.vue'),
    meta: { requiresAuth: true, tab: APP_TAB.HOME },
  },
  {
    path: '/fluid',
    component: () => import('@/features/fluid/components/FluidView.vue'),
    meta: { requiresAuth: true, tab: APP_TAB.FLUID },
  },
  {
    path: '/fluid/history',
    component: () => import('@/features/fluid/components/ReadingHistoryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/symptoms',
    component: () => import('@/features/symptoms/components/SymptomsView.vue'),
    meta: { requiresAuth: true, tab: APP_TAB.SYMPTOMS },
  },
  {
    path: '/diet',
    component: () => import('@/features/nutrition/components/NutritionView.vue'),
    meta: { requiresAuth: true, tab: APP_TAB.DIET },
  },
  {
    path: '/symptoms/history',
    component: () => import('@/features/symptoms/components/SymptomHistoryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/symptoms/assessment/:id',
    component: () => import('@/features/symptoms/components/SymptomAssessmentView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/diet/diary',
    component: () => import('@/features/nutrition/components/FoodDiaryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/diet/diary/:id',
    component: () => import('@/features/nutrition/components/MealDetailsView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/diet/guide',
    component: () => import('@/features/nutrition/components/FoodGuideView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/diet/guide/:mainUnique',
    component: () => import('@/features/resources/components/TreatmentContentView.vue'),
    props: (route) => ({
      mainUnique: route.params.mainUnique,
      back: '/diet/guide',
      source: TREATMENT_CONTENT_SOURCE.FOOD_GUIDE,
    }),
    meta: { requiresAuth: true },
  },
  {
    path: '/diet/weekly',
    component: () => import('@/features/nutrition/components/WeeklySummaryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/more',
    component: () => import('@/features/resources/components/MoreView.vue'),
    meta: { requiresAuth: true, tab: APP_TAB.MORE },
  },
  {
    path: '/education',
    component: () => import('@/features/resources/components/TreatmentContentListView.vue'),
    props: { titleKey: 'nkf.education', back: '/more', articleBasePath: '/education' },
    meta: { requiresAuth: true },
  },
  {
    path: '/education/:mainUnique',
    component: () => import('@/features/resources/components/TreatmentContentView.vue'),
    props: (route) => ({ mainUnique: route.params.mainUnique, back: '/education' }),
    meta: { requiresAuth: true },
  },
  {
    path: '/home/self-care-tips',
    component: () => import('@/features/resources/components/TreatmentContentListView.vue'),
    props: {
      titleKey: 'nkf.homeCards.recommended',
      back: '/home',
      articleBasePath: '/home/self-care-tips',
      source: TREATMENT_CONTENT_SOURCE.SELF_CARE_TIPS,
    },
    meta: { requiresAuth: true },
  },
  {
    path: '/home/self-care-tips/:mainUnique',
    component: () => import('@/features/resources/components/TreatmentContentView.vue'),
    props: (route) => ({
      mainUnique: route.params.mainUnique,
      back: '/home/self-care-tips',
      source: TREATMENT_CONTENT_SOURCE.SELF_CARE_TIPS,
    }),
    meta: { requiresAuth: true },
  },
  {
    path: '/article/:mainUnique',
    component: () => import('@/features/resources/components/TreatmentContentView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/home/progress',
    component: () => import('@/features/resources/components/DailyGoalsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/goals',
    component: () => import('@/features/resources/components/GoalsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/medications',
    component: () => import('@/features/resources/components/MedicationView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reminders',
    component: () => import('@/features/resources/components/RemindersView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/blood-results',
    component: () => import('@/features/resources/components/BloodResultsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support',
    component: () => import('@/features/resources/components/SupportView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/blood-results/trends',
    component: () => import('@/features/resources/components/BloodTrendsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/faq',
    component: () => import('@/features/resources/components/FaqView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support/social',
    component: () => import('@/features/resources/components/SocialView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support/care-team',
    component: () => import('@/features/resources/components/CareContactsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support/emotional-health',
    component: () => import('@/features/symptoms/components/MoodHistoryView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/support/emotional-health/:id',
    component: () => import('@/features/symptoms/components/MoodDetailView.vue'),
    props: true,
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    component: () => import('@/features/profile/components/ProfileView.vue'),
    meta: { requiresAuth: true },
  },
  ...authenticationRoutes,
  { path: '/:pathMatch(.*)*', redirect: '/home' },
];
export default routes;
